import React, { useEffect, useState } from 'react';
import DateRange from 'components/common/general/DateRange';
import { Button, colors, Toast } from '@insurancemenu/imenu-components-lib';
import { CloudUpload } from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import ActionModal from '@insurancemenu/imenu-components-lib/dist/components/modals/ActionModal';
import { ModalType } from '@insurancemenu/imenu-components-lib/dist/helpers/ModalsHelper';
import UploadFilesTable from '../UploadFilesTable';
import { createStyles } from './styles';
import UploadSyncFileModal from '../UploadSyncFileModal';
import SponsorSelect from '../SponsorSelect';

const UploadFilesContent: React.FC = observer(() => {
  const classes = createStyles();

  const {
    userStore: { sponsorId: currentSponsorId },
    uploadSyncFilesStore: {
      isWarningModalOpen,
      uploadId,
      fileUploads,
      loading,
      sponsorId,
      date: { from: dateFrom, to: dateTo },
      toastInfo: { showToast, toastType, toastMessage },
      updateDate,
      setSponsorId,
      deleteUpload,
      closeWarningModal,
      getAllDomains,
      getFileUploads,
      setIsFileUploadReset,
      closeToast,
    },
  } = useRootStore();

  const [showUploadModal, setShowUploadModal] = useState(false);

  const convertedFileUploads = fileUploads.map((upload) => toJS(upload)).slice();
  const removedFileName = convertedFileUploads.find((upload) => upload.id === uploadId)?.fileName || 'this';

  useEffect(() => {
    void getAllDomains();
    if (currentSponsorId) setSponsorId(currentSponsorId);
  }, []);

  useEffect(() => {
    if (sponsorId) {
      void getFileUploads(dateFrom, dateTo, sponsorId);
    }
  }, [dateFrom, dateTo, sponsorId]);

  const updateTableByDateRange = (from: string, to: string): void => {
    updateDate(from, to);
  };

  const onUploadRemove = async (): Promise<void> => {
    await deleteUpload(uploadId);
    closeWarningModal();
    void getFileUploads(dateFrom, dateTo, sponsorId);
  };

  return (
    <div className={classes.uploadContent}>
      <Button
        onClick={(): void => {
          setIsFileUploadReset(false);
          setShowUploadModal(true);
        }}
        size="large"
        color="primary"
        leftIconProp={<CloudUpload />}
        className={classes.uploadButton}
      >
        Upload Files
      </Button>
      <div className={classes.headerInfo}>
        <div className={classes.dateRange}>
          <DateRange updateTableByDateRange={updateTableByDateRange} showAdminToggle={false} />
        </div>
        <SponsorSelect
          value={sponsorId}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
            setSponsorId(e.target.value);
          }}
        />
      </div>
      <UploadFilesTable />
      <UploadSyncFileModal
        show={showUploadModal}
        sponsorId={sponsorId}
        onClose={(): void => {
          setShowUploadModal(false);
        }}
      />
      <ActionModal
        show={isWarningModalOpen}
        title="Delete File Upload"
        subtitle={
          <p>
            Are you sure you want to delete <b className={classes.removedFileName}>{removedFileName}</b> file?
          </p>
        }
        actionButtonText="Delete Upload"
        closeButtonText="Cancel"
        onClose={closeWarningModal}
        onAction={onUploadRemove}
        modalType={ModalType.DELETE}
        loading={loading}
      />
      {toastType === 'WARNING' && (
        <Toast show={showToast} onClose={closeToast} backgroundColor={colors.yellow[600]}>
          {toastMessage}
        </Toast>
      )}
      {toastType === 'SUCCESS' && (
        <Toast show={showToast} onClose={closeToast} colorType={ToastColorTypes.SUCCESS}>
          {toastMessage}
        </Toast>
      )}
    </div>
  );
});

export default UploadFilesContent;
